/**
 * GmailConnector — connect to Gmail via Google API OAuth.
 *
 * Auth: oauth:google:<account> — managed by OAuthSecretProvider.
 * Peer dependency: googleapis (lazy-loaded).
 */

import type {
  ConnectContext,
  ConnectorDeclaration,
  ConnectorFieldSpec,
  ConnectorHandle,
  OperationDescriptor,
  RuntimeSkillDescriptor,
  ToolFace,
} from "@skaile/workspaces/connectors";
import { AbstractConnector } from "@skaile/workspaces/connectors";

interface GmailState {
  account: string;
  accessToken: string;
  gmail: any; // googleapis gmail client
}

/**
 * Connector for Gmail — provides read and send access to email messages and drafts via the Google API.
 * Authenticates using OAuth2 (managed by OAuthSecretProvider). Requires the `googleapis` optional peer dependency.
 * @docLink packages/factory-assets/concepts#gmail-connector
 */
export class GmailConnector extends AbstractConnector {
  readonly name = "gmail";

  tools: ToolFace;

  constructor() {
    super();
    this.tools = {
      describeOperations: (handle) => this._describeOperations(handle),
      executeOp: (handle, operation, args) => this._executeOp(handle, operation, args),
      describeSkill: (handle, declaration) => this._describeSkill(handle, declaration),
    };
  }

  describeFields(): ConnectorFieldSpec[] {
    return [
      {
        key: "account",
        label: "Gmail Account",
        description: "Email address — used to label this connector and scope the OAuth token",
        type: "text",
        required: true,
        sensitive: false,
        group: "Connection",
      },
      {
        key: "auth",
        label: "Authentication",
        description: "OAuth access token for this Gmail account. Click to authorize.",
        type: "oauth",
        oauthService: "google",
        required: true,
        sensitive: true,
        group: "Authentication",
      },
    ];
  }

  async connect(declaration: ConnectorDeclaration, ctx: ConnectContext): Promise<ConnectorHandle> {
    const account = ctx.resolvedFields?.account ?? String(declaration.options?.account ?? "");
    const accessToken =
      ctx.resolvedFields?.auth ?? ctx.secrets?.resolve(declaration.auth ?? "") ?? "";

    if (!accessToken) {
      this.log.warn("OAuth access token missing or refresh failed", { account });
      throw new Error(`GmailConnector requires OAuth access token for account "${account}"`);
    }

    this.log.info("connect", { account });
    const googleapisPkg = "googleapis";
    const { google } = (await import(/* @vite-ignore */ googleapisPkg)) as any;
    const auth = new google.auth.OAuth2();
    auth.setCredentials({ access_token: accessToken });
    const gmail = google.gmail({ version: "v1", auth });

    return this.makeHandle(declaration, { account, accessToken, gmail } as GmailState);
  }

  async disconnect(handle: ConnectorHandle): Promise<void> {
    this.log.info("disconnect");
    // No network teardown required for Gmail OAuth — token expiry is managed externally.
    handle.status = "disconnected";
  }

  // ── Tool face methods (private, wired in constructor) ─────────────────────

  private _describeSkill(
    _handle: ConnectorHandle,
    declaration: ConnectorDeclaration,
  ): RuntimeSkillDescriptor {
    const account = String(declaration.options?.account ?? "unknown");
    return {
      id: declaration.id,
      name: `Gmail — ${account}`,
      description: `Read and write email for ${account}`,
      category: "email",
      access: declaration.access,
      toolNames:
        declaration.access === "read-write"
          ? ["list_messages", "get_message", "send_message", "search_messages", "create_draft"]
          : ["list_messages", "get_message", "search_messages"],
    };
  }

  private _describeOperations(handle: ConnectorHandle): OperationDescriptor[] {
    const ops: OperationDescriptor[] = [
      {
        name: "list_messages",
        description: "List messages in the inbox or a label",
        accessLevel: "read",
        args: [
          {
            name: "label",
            type: "string",
            required: false,
            description: "Label ID (default: INBOX)",
          },
          {
            name: "limit",
            type: "number",
            required: false,
            description: "Max results (default: 20)",
          },
        ],
      },
      {
        name: "get_message",
        description: "Fetch a full message by ID",
        accessLevel: "read",
        args: [{ name: "id", type: "string", required: true, description: "Message ID" }],
      },
      {
        name: "search_messages",
        description: "Search messages using Gmail query syntax",
        accessLevel: "read",
        args: [
          { name: "query", type: "string", required: true, description: "Gmail search query" },
        ],
      },
    ];

    if (handle.access === "read-write") {
      ops.push(
        {
          name: "send_message",
          description: "Send an email",
          accessLevel: "write",
          args: [
            { name: "to", type: "string", required: true, description: "Recipient address" },
            { name: "subject", type: "string", required: true, description: "Subject line" },
            { name: "body", type: "string", required: true, description: "Message body" },
            {
              name: "html",
              type: "boolean",
              required: false,
              description: "Set to true if body is HTML",
            },
          ],
        },
        {
          name: "create_draft",
          description: "Create a draft without sending",
          accessLevel: "write",
          args: [
            { name: "to", type: "string", required: true, description: "Recipient address" },
            { name: "subject", type: "string", required: true, description: "Subject line" },
            { name: "body", type: "string", required: true, description: "Message body" },
          ],
        },
      );
    }

    return ops;
  }

  private async _executeOp(
    handle: ConnectorHandle,
    operation: string,
    args: Record<string, unknown>,
  ): Promise<string> {
    const { gmail, account } = handle.state as GmailState;

    switch (operation) {
      case "list_messages": {
        const res = await gmail.users.messages.list({
          userId: "me",
          labelIds: [String(args.label ?? "INBOX")],
          maxResults: Number(args.limit ?? 20),
        });
        return JSON.stringify(res.data.messages ?? [], null, 2);
      }
      case "get_message": {
        const res = await gmail.users.messages.get({
          userId: "me",
          id: String(args.id),
          format: "full",
        });
        return JSON.stringify(res.data, null, 2);
      }
      case "search_messages": {
        const res = await gmail.users.messages.list({ userId: "me", q: String(args.query) });
        return JSON.stringify(res.data.messages ?? [], null, 2);
      }
      case "send_message": {
        const raw = this._buildRawMessage(
          account,
          String(args.to),
          String(args.subject),
          String(args.body),
          Boolean(args.html),
        );
        await gmail.users.messages.send({ userId: "me", requestBody: { raw } });
        return "Message sent.";
      }
      case "create_draft": {
        const raw = this._buildRawMessage(
          account,
          String(args.to),
          String(args.subject),
          String(args.body),
          false,
        );
        const res = await gmail.users.drafts.create({
          userId: "me",
          requestBody: { message: { raw } },
        });
        return `Draft created: ${res.data.id}`;
      }
      default:
        throw new Error(`GmailConnector has no operation "${operation}"`);
    }
  }

  private _buildRawMessage(
    from: string,
    to: string,
    subject: string,
    body: string,
    html: boolean,
  ): string {
    const contentType = html ? "text/html" : "text/plain";
    const message = [
      `From: ${from}`,
      `To: ${to}`,
      `Subject: ${subject}`,
      `MIME-Version: 1.0`,
      `Content-Type: ${contentType}; charset=utf-8`,
      "",
      body,
    ].join("\n");
    return Buffer.from(message).toString("base64url");
  }
}

/**
 * Creates a new GmailConnector instance.
 * @returns Configured GmailConnector ready to be registered in the connector registry.
 * @docLink packages/factory-assets/api-reference#gmail-connector-factory
 */
export function createConnector(): GmailConnector {
  return new GmailConnector();
}
